import React from "react";

import { Button } from "@portex-pro/ui-components";
import StepCollapse from "app/pages/quote-request/_components/StepCollapse";
import { useCreateStepCollapseProps } from "app/pages/quote-request/_hooks/useStepCollapse";
import { useQuoteRequestSelector } from "app/pages/quote-request/_store";
import { quoteRequestActions } from "app/pages/quote-request/_store/quoteRequestSliceV2";
import { QuoteStepIndex } from "app/pages/quote-request/types";
import startCase from "lodash/startCase";
import { useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { FinalSchema } from "../../_schemas";
import { AdditionalInfoSection } from "./AdditionalInfoSection";
import { CargoSection } from "./CargoSection";
import { ReferenceNumberSection } from "./ReferenceNumberSection";

const ShipmentStepTag: React.FC = () => {
  const methods = useFormContext<FinalSchema>();

  const trailer_type = useWatch({ control: methods.control, name: "trailer_type" });
  const packageGroups = useWatch({ control: methods.control, name: "ltl_load_spec.package_groups" });

  const totalWeight =
    packageGroups?.reduce((total, group) => {
      if (group?.weight_per_package && group?.item_quantity && group?.weight_type === "per") {
        return total + group.weight_per_package * group.item_quantity;
      }

      if (group?.weight_per_package && group?.weight_type === "total") {
        return total + group.weight_per_package;
      }

      return total;
    }, 0) || 0;

  const firstItemType = packageGroups?.[0]?.packaging_type;

  if (!trailer_type && !totalWeight && !firstItemType) {
    return <></>;
  }

  const parts: string[] = [];

  if (trailer_type) {
    const typeText = trailer_type === "DRY_VAN" ? "Dry Van" : startCase(trailer_type.toLowerCase().replace(/_/g, " "));
    parts.push(typeText);
  }

  let packingInformation = "";

  if (totalWeight > 0) {
    const formattedWeight = totalWeight.toLocaleString();
    packingInformation += `with ${formattedWeight} lb `;
  }

  if (firstItemType) {
    const itemText = startCase(firstItemType.toLowerCase().replace(/_/g, " "));
    packingInformation += `of ${itemText.toLowerCase()}`;
  }

  const displayText = parts.join(" ");

  if (!displayText) {
    return <></>;
  }

  return (
    <div className="inline-flex items-center bg-blue-50 rounded-md px-2 py-1">
      <span className="text-blue-600 font-bold text-sm">{displayText}</span>
      <span className="text-blue-600 text-sm">&nbsp;{`${packingInformation}`}</span>
    </div>
  );
};

export const ShipmentStep: React.FC<{ onSubmit: (data: FinalSchema) => void }> = ({ onSubmit }) => {
  const { t } = useTranslation(["common", "quoteRequest"]);
  const setStepIndex = quoteRequestActions.useSetStepIndex();
  const { stepIndex } = useQuoteRequestSelector((store) => store.quoteRequestSliceV2);
  const { baseStepCollapseProps, setIsOpen } = useCreateStepCollapseProps({
    expectedStepIndex: QuoteStepIndex.Shipment,
    name: t("common:shipmentDetails"),
    number: 2,
  });

  const methods = useFormContext<FinalSchema>();

  const submit = (data: FinalSchema) => {
    onSubmit(data);
    setStepIndex(QuoteStepIndex.Partners);
    setIsOpen(false);
  };

  return (
    <StepCollapse
      {...baseStepCollapseProps}
      tag={<ShipmentStepTag />}
      footer={
        stepIndex <= QuoteStepIndex.Shipment ? (
          <div className="flex justify-end mt-2 w-full">
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={methods.handleSubmit(submit)}
              style={{ width: 150 }}
            >
              {t("common:next")}
            </Button>
          </div>
        ) : null
      }
    >
      <div className="my-6 w-full flex flex-col">
        <ReferenceNumberSection />
        <CargoSection />
        <AdditionalInfoSection />
      </div>
    </StepCollapse>
  );
};
